import { useState } from 'react';
import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '@heroui/react';
import { Phone } from 'lucide-react';
import CustomButton from '../../CustomButton';
import PhoneInput from '../../PhoneInput';
import { useAuth } from '../../../../context/AuthContext';
import { useUpdateUserMobile } from '../../../../hook/useMutationData';
import { useTranslation } from 'react-i18next';

/**
 * Normalize a Canadian phone number to E.164 (e.g. "+14165551234").
 * Cognito's phone_number requires strict E.164 with no spaces, so the
 * space-separated value PhoneInput emits ("+1 4165551234") must be cleaned
 * before it reaches the backend (which forwards it to Cognito).
 */
function toE164Canada(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return null;
  return `+1${national}`;
}

/**
 * Post-login phone collection. Renders for any authenticated user whose
 * account has no phone number (social signups via Hosted UI never capture
 * one). Self-gates on `needsPhone`, so it stays invisible otherwise and can
 * be mounted globally.
 */
const CollectPhoneModal = () => {
  const { t } = useTranslation();
  const { needsPhone, userId, refreshUser, user, role } = useAuth();
  const { mutate, isPending } = useUpdateUserMobile();

  const [raw, setRaw] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const e164 = toE164Canada(raw);
    if (!e164) {
      setError(t('phone_modal.invalid_number'));
      return;
    }
    if (!userId) {
      setError(t('phone_modal.session_loading'));
      return;
    }
    setError('');

    mutate(
      { userId, mobile: e164, role: role || '', email: user?.email || '' },
      {
        onSuccess: () => {
          refreshUser();
          setRaw('');
          addToast({
            title: t('phone_modal.saved'),
            radius: 'md',
            color: 'success',
          });
        },
        onError: (err: any) => {
          console.log('error: ', err);
          addToast({
            title: t('phone_modal.could_not_save'),
            description:
              err?.response?.data?.message || t('phone_modal.generic_error'),
            radius: 'md',
            color: 'danger',
          });
        },
      }
    );
  };

  return (
    <Modal
      isOpen={needsPhone}
      isDismissable={false}
      isKeyboardDismissDisabled
      hideCloseButton
      size="md"
      placement="center"
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Phone className="h-4 w-4 text-primary" />
          </span>
          <div className="flex flex-col">
            <h3 className="text-base font-semibold text-foreground">
              {t('phone_modal.title')}
            </h3>
            <p className="text-xs font-normal text-muted-foreground">
              {t('phone_modal.subtitle')}
            </p>
          </div>
        </ModalHeader>

        <ModalBody>
          <PhoneInput
            name="mobile"
            value={raw}
            isInvalid={!!error}
            errorMessage={error}
            onChange={(e) => {
              setRaw(e.target.value);
              if (error) setError('');
            }}
          />
        </ModalBody>

        <ModalFooter>
          <CustomButton
            color="primary"
            fullWidth
            isLoading={isPending}
            isDisabled={isPending}
            onPress={handleSubmit}
          >
            {t('phone_modal.save')}
          </CustomButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CollectPhoneModal;
